import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, Fragment } from "react";
import { Bell, BarChart3, ChevronRight, Check, AlertTriangle } from "lucide-react";
import { useT, useLang, lookup } from "@/i18n";
import {
  useNotifications,
  useMarkAllRead,
  type NotificationRow,
} from "@/hooks/useNotifications";
import { ALL_BADGES, localizeBadge, type BadgeTier, type BadgeDef } from "@/lib/badges";
import { BadgeIcon } from "@/components/BadgeIcon";
import { BadgePreviewModal } from "@/components/BadgePreview";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { translateNotification } from "@/lib/notificationTranslator";


export const Route = createFileRoute("/_app/notifications")({
  component: NotificationsPage,
});

type PointsMeta = {
  match_id?: number;
  points_earned?: number;
  home_code?: string;
  away_code?: string;
  home_score?: number;
  away_score?: number;
  home_pen_score?: number | null;
  away_pen_score?: number | null;
  prediction_home?: number;
  prediction_away?: number;
  first_scorer_country?: string;
  first_scorer_player?: string;
  breakdown?: {
    correct_result?: boolean;
    exact_score?: boolean;
    home_goals?: boolean;
    away_goals?: boolean;
    goal_diff?: boolean;
    first_team?: boolean;
    first_scorer?: boolean;
  };
};

const TIER_META: Record<
  BadgeTier,
  { icon: string; labelEn: string }
> = {
  easy: { icon: "🟢", labelEn: "Easy" },
  medium: { icon: "🟡", labelEn: "Medium" },
  hard: { icon: "🔴", labelEn: "Hard" },
  legendary: { icon: "🏆", labelEn: "Legendary" },
  god: { icon: "⚡", labelEn: "God Mode" },
};

function relativeTime(iso: string, lang: string): string {
  const date = new Date(iso);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return lang === "ro" ? "acum" : "now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return lang === "ro" ? `acum ${m}m` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return lang === "ro" ? `acum ${h}h` : `${h}h ago`;
  }
  return date.toLocaleDateString(lang === "ro" ? "ro-RO" : "en-GB", {
    day: "numeric",
    month: "short",
  });
}

function exactDateTime(iso: string, lang: string): string {
  return new Date(iso).toLocaleString(lang === "ro" ? "ro-RO" : "en-GB", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function sectionLabel(iso: string, lang: "en" | "ro"): string {
  const now = new Date();
  const d = new Date(iso);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (today.getTime() - that.getTime()) / 86400000
  );
  if (diffDays <= 0) return lookup(lang, "Today");
  if (diffDays === 1) return lookup(lang, "Yesterday");
  return d.toLocaleDateString(lang === "ro" ? "ro-RO" : "en-GB", {
    day: "numeric",
    month: "short",
  });
}


function NotificationsPage() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const { data, isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();
  const { user } = useAuth();
  const items = data ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [previewBadgeId, setPreviewBadgeId] = useState<string | null>(null);
  const [previewEarnedAt, setPreviewEarnedAt] = useState<string | null>(null);

  useEffect(() => {
    if (items.some((n) => !n.is_read)) {
      markAllRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  async function onClick(n: NotificationRow) {
    if (n.type === "badge_earned") {
      const id = (n.metadata as { badge_id?: string })?.badge_id;
      if (!id) return;
      // Open the informational badge detail modal — NEVER the confetti
      // celebration. The celebration is a one-time event when the badge is
      // first earned; tapping a notification is a "review" action.
      setPreviewBadgeId(id);
      setPreviewEarnedAt(null);
      if (user?.id) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: row } = await (supabase as any)
            .from("user_badges")
            .select("earned_at")
            .eq("user_id", user.id)
            .eq("badge_id", id)
            .maybeSingle();
          if (row?.earned_at) {
            setPreviewEarnedAt(
              new Date(row.earned_at).toLocaleDateString(
                lang === "ro" ? "ro-RO" : "en-US",
                { day: "numeric", month: "short", year: "numeric" }
              )
            );
          }
        } catch {
          // non-fatal
        }
      }
    } else if (n.type === "points_awarded") {
      setExpandedId((prev) => (prev === n.id ? null : n.id));
    }
  }


  // Group by section label (Today / Yesterday / date)
  const groups: Array<{ key: string; label: string; rows: NotificationRow[] }> = [];
  let lastKey = "";
  for (const n of items) {
    const key = dayKey(n.created_at);
    if (key !== lastKey) {
      groups.push({
        key,
        label: sectionLabel(n.created_at, lang),
        rows: [],
      });
      lastKey = key;
    }
    groups[groups.length - 1].rows.push(n);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">
          {t("Notifications", undefined, "Notificări")}
        </h1>
        {items.some((n) => !n.is_read) && (
          <button
            onClick={() => markAllRead.mutate()}
            className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-premium"
          >
            <Check className="h-3.5 w-3.5" />
            {t("Mark all as read", undefined, "Marchează toate ca citite")}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="rounded-2xl border bg-card py-16 text-center text-sm text-muted-foreground">
          {t("Loading…", undefined, "Se încarcă…")}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card/40 py-20">
          <Bell className="h-12 w-12 text-muted-foreground/60" />
          <p className="mt-4 text-sm text-muted-foreground">
            {t("No notifications yet", undefined, "Nicio notificare încă")}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-5">
            {groups.map((g) => (
              <section key={g.key}>
                <h2 className="mb-2 border-l-2 border-premium pl-2 text-xs font-bold uppercase tracking-wider text-premium">
                  {g.label}
                </h2>
                <ul className="space-y-2">
                  {g.rows.map((n) => (
                    <NotificationRowItem
                      key={n.id}
                      n={n}
                      lang={lang}
                      expanded={expandedId === n.id}
                      onClick={() => onClick(n)}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}

      {previewBadgeId && (() => {
        const def = ALL_BADGES.find((b) => b.id === previewBadgeId);
        if (!def) return null;
        const localized = localizeBadge(def as BadgeDef, lang);
        return (
          <BadgePreviewModal
            open
            badgeId={def.id}
            badgeName={localized.name}
            flavor={localized.flavor}
            tier={def.tier}
            earnedAt={previewEarnedAt}
            locked={false}
            onClose={() => {
              setPreviewBadgeId(null);
              setPreviewEarnedAt(null);
            }}
          />
        );
      })()}
    </div>
  );
}


function TierPill({ tier, lang }: { tier: BadgeTier; lang: "en" | "ro" }) {
  const meta = TIER_META[tier];
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-premium/30 bg-premium/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-premium">
      <span className="leading-none">{meta.icon}</span>
      <span>{lookup(lang, meta.labelEn)}</span>
    </span>
  );
}

function NotificationRowItem({
  n,
  lang,
  expanded,
  onClick,
}: {
  n: NotificationRow;
  lang: "en" | "ro";
  expanded: boolean;
  onClick: () => void;
}) {
  const unread = !n.is_read;
  const exact = exactDateTime(n.created_at, lang);
  const isPoints = n.type === "points_awarded";
  const isBadge = n.type === "badge_earned";
  const isMissed = n.type === "prediction_missed";

  const badgeId = isBadge
    ? (n.metadata as { badge_id?: string })?.badge_id
    : undefined;
  const badgeDef = badgeId
    ? ALL_BADGES.find((b) => b.id === badgeId)
    : undefined;

  let leftVisual: React.ReactNode = (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60 text-base">
      <Bell className="h-4 w-4 text-primary" />
    </div>
  );

  if (badgeDef) {
    leftVisual = (
      <BadgeIcon id={badgeDef.id} size="sm" />
    );
  } else if (isPoints) {
    leftVisual = (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60 text-base leading-none">
        <span aria-hidden>⚽</span>
      </div>
    );
  } else if (isMissed) {
    leftVisual = (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30">
        <AlertTriangle className="h-4 w-4" />
      </div>
    );
  } else if (n.type === "league_position_changed") {
    leftVisual = (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/60">
        <BarChart3 className="h-4 w-4 text-primary" />
      </div>
    );
  }

  const translated = translateNotification(n, lang);

  // Title rendering:
  //   badge          → gold name + tier pill
  //   missed         → muted (no points to celebrate)
  //   default        → plain foreground
  let titleNode: React.ReactNode;
  if (badgeDef) {
    const localizedName = lookup(lang, badgeDef.name);
    titleNode = (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[15px] font-bold text-premium break-words">
          {localizedName}
        </span>
        <TierPill tier={badgeDef.tier} lang={lang} />
      </div>
    );
  } else if (isMissed) {
    titleNode = (
      <div className="text-[15px] font-semibold text-foreground/70 break-words">
        {translated.title}
      </div>
    );
  } else {
    titleNode = (
      <div className="text-[15px] font-semibold text-foreground break-words">
        {translated.title}
      </div>
    );
  }

  const localizedBody = translated.body;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        title={exact}
        aria-expanded={isPoints ? expanded : undefined}
        disabled={isMissed}
        className={`flex w-full items-stretch gap-3 rounded-t-xl border p-3 text-left shadow-sm transition-colors ${
          isMissed ? "cursor-default" : "hover:bg-accent/40"
        } ${
          expanded ? "rounded-b-none" : "rounded-b-xl"
        } ${
          unread
            ? "border-l-[2px] border-l-premium bg-premium/[0.06]"
            : "bg-card"
        } ${isMissed ? "border-l-[2px] border-l-amber-500" : ""}`}
      >
        <div className="flex items-center">{leftVisual}</div>
        <div className="flex-1 min-w-0 self-center">
          {titleNode}
          {localizedBody && (
            <div className="mt-0.5 text-[13px] text-muted-foreground break-words">
              {localizedBody}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-4 self-center">
          <div
            className="text-[11px] text-muted-foreground/70 tabular-nums"
            title={exact}
          >
            {relativeTime(n.created_at, lang)}
          </div>
          {isPoints && (
            <ChevronRight
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                expanded ? "rotate-90" : ""
              }`}
            />
          )}
        </div>
      </button>
      {isPoints && expanded && (
        <PointsBreakdownInline
          meta={n.metadata as PointsMeta}
          lang={lang}
        />
      )}
    </li>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      aria-hidden
      className={`inline-block h-2.5 w-2.5 rounded-full ${
        ok ? "bg-emerald-500 shadow-[0_0_0_2px_rgba(16,185,129,0.18)]" : "bg-muted-foreground/50"
      }`}
    />
  );
}

function PointsBreakdownInline({
  meta,
  lang,
}: {
  meta: PointsMeta;
  lang: string;
}) {
  const isRO = lang === "ro";
  const pts = isRO ? "pct" : "pts";
  const b = meta.breakdown ?? {};
  const rows: Array<{ ok: boolean; label: string; pts: number }> = [
    { ok: !!b.correct_result, label: isRO ? "Rezultat corect" : "Correct result", pts: 10 },
    { ok: !!b.exact_score, label: isRO ? "Scor exact" : "Exact score", pts: 15 },
    { ok: !!b.first_team, label: isRO ? "Prima țară marcatoare" : "First team to score", pts: 5 },
    { ok: !!b.first_scorer, label: isRO ? "Primul marcator" : "First scorer", pts: 10 },
    { ok: !!b.home_goals, label: isRO ? "Goluri acasă corecte" : "Home goals correct", pts: 5 },
    { ok: !!b.away_goals, label: isRO ? "Goluri deplasare corecte" : "Away goals correct", pts: 5 },
    { ok: !!b.goal_diff, label: isRO ? "Golaveraj" : "Goal difference", pts: 5 },
  ];

  const home = meta.home_code ?? "?";
  const away = meta.away_code ?? "?";
  const ph = meta.prediction_home;
  const pa = meta.prediction_away;
  const fsc = meta.first_scorer_country;
  const fsp = meta.first_scorer_player;
  const hps = meta.home_pen_score;
  const aps = meta.away_pen_score;
  const showFooter = ph !== undefined || pa !== undefined || fsc || fsp;

  return (
    <div className="overflow-hidden rounded-b-xl border border-t-0 bg-card/60 p-2 animate-in fade-in slide-in-from-top-1">
      <div className="rounded-lg bg-background/60 px-4 py-3 text-[13px] ring-1 ring-border/60">
        <ul className="divide-y divide-border/50">
          {rows.map((r) => (
            <li
              key={r.label}
              className="flex items-center justify-between gap-3 py-2"
            >
              <span className="flex items-center gap-2.5">
                <StatusDot ok={r.ok} />
                <span className="font-normal text-foreground">{r.label}</span>
              </span>
              <span
                className={`font-bold tabular-nums ${
                  r.ok ? "text-premium" : "text-muted-foreground/70"
                }`}
              >
                +{r.ok ? r.pts : 0} {pts}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex items-center justify-between border-t border-premium/30 pt-2.5 text-[15px] font-black">
          <span className="text-premium">{isRO ? "Total" : "Total"}</span>
          <span className="text-premium">
            +{meta.points_earned ?? 0} {pts}
          </span>
        </div>
        {showFooter && (
          <div className="mt-3 text-xs italic text-muted-foreground/80 break-words">
            {isRO ? "Pronosticul tău: " : "Your prediction: "}
            <span className="not-italic font-semibold text-foreground/90">
              {home} {ph ?? "?"}–{pa ?? "?"} {away}
            </span>
            {hps != null && aps != null && (
              <Fragment>
                {" · "}
                {isRO ? "Penalty-uri: " : "Penalties: "}
                <span className="not-italic font-semibold text-foreground/90">
                  {home} {hps}–{aps} {away}
                </span>
              </Fragment>
            )}
            {fsc && (
              <Fragment>
                {" · "}
                {isRO ? "Prima țară marcatoare: " : "First team to score: "}
                <span className="not-italic font-semibold text-foreground/90">
                  {fsc}
                </span>
              </Fragment>
            )}
            {fsp && (
              <Fragment>
                {" · "}
                {isRO ? "Marcator: " : "Scorer: "}
                <span className="not-italic font-semibold text-foreground/90">
                  {fsp}
                </span>
              </Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
